/**
 * API Execution Panel — right-side panel showing API test execution results
 * with Chrome DevTools-style detail tabs (Headers, Payload, Response, Timing)
 * and a Summary tab with aggregate statistics.
 */
import React, { useState, useRef, useEffect, useMemo } from 'react';
import {
  Box,
  Typography,
  Chip,
  Collapse,
  IconButton,
  LinearProgress,
  Alert,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
} from '@mui/material';
import {
  CheckCircle as PassedIcon,
  Cancel as FailedIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  HourglassEmpty as RunningIcon,
  Security as SecurityIcon,
  Lock as LockIcon,
  Api as ApiIcon,
  Speed as SpeedIcon,
  Assessment as AssessmentIcon,
  ContentCopy as CopyIcon,
  Check as CheckIcon,
  FilterList as FilterIcon,
  Code as CodeIcon,
  Science as ScienceIcon,
  FolderOpen as FolderOpenIcon,
  Storage as StorageIcon,
  Description as FileIcon,
  OpenInNew as OpenInNewIcon,
} from '@mui/icons-material';
import { APIStepResult, GeneratedCodePayload, AgentToolActivity, AgentInvestigationFindings, TMSIntegrationInfo, TMSTestRun, SlavePreflightEndpoint } from '../types';
import { listWorkspaceFiles, getWorkspaceFileContent } from '../services/api.service';
import GeneratedCodeViewer from './GeneratedCodeViewer';
import AgentActivityPanel from './AgentActivityPanel';

// ============================================================================
// Helpers
// ============================================================================

const METHOD_COLORS: Record<string, string> = {
  GET: '#2e7d32', POST: '#1565c0', PUT: '#e65100',
  PATCH: '#6a1b9a', DELETE: '#c62828', HEAD: '#37474f', OPTIONS: '#757575',
};

const STATUS_REASONS: Record<number, string> = {
  200: 'OK',
  201: 'Created',
  204: 'No Content',
  301: 'Moved Permanently',
  302: 'Found',
  304: 'Not Modified',
  400: 'Bad Request',
  401: 'Unauthorized',
  403: 'Forbidden',
  404: 'Not Found',
  405: 'Method Not Allowed',
  409: 'Conflict',
  422: 'Validation Error',
  429: 'Rate Limited',
  500: 'Internal Server Error',
  502: 'Bad Gateway',
  503: 'Service Unavailable',
  504: 'Gateway Timeout',
};

const STATUS_DETAIL: Record<number, string> = {
  401: 'Missing or invalid auth token',
  403: 'Insufficient permissions',
  429: 'Too many requests',
};

/** Parse query string parameters from a URL */
const parseQueryParams = (url?: string): [string, string][] => {
  if (!url) return [];
  try {
    const u = new URL(url, 'http://placeholder');
    return Array.from(u.searchParams.entries());
  } catch {
    return [];
  }
};

/** Estimate byte size of a JSON body */
const estimateSize = (body: any): string => {
  if (!body) return '';
  const str = typeof body === 'string' ? body : JSON.stringify(body);
  const bytes = new Blob([str]).size;
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

// ============================================================================
// Test Verdict Logic
// ============================================================================

/**
 * Categories where a 4XX/5XX response is the EXPECTED outcome.
 * Negative tests probe error handling (invalid input, missing auth, etc.)
 * and security tests probe vulnerability resistance — both SHOULD return errors.
 */
const EXPECTED_FAIL_CATEGORIES = new Set(['negative', 'security']);

type TestVerdict = 'pass' | 'fail' | 'expected_fail' | 'unexpected_pass' | 'running' | 'error';

/**
 * Determine the true test verdict considering the test category.
 *
 *   Functional test + 2XX  →  pass
 *   Functional test + 4XX+ →  fail           (real bug)
 *   Negative test   + 4XX+ →  expected_fail  (API correctly rejected)
 *   Negative test   + 2XX  →  unexpected_pass (API should have rejected but didn't)
 */
const getVerdict = (r: APIStepResult): TestVerdict => {
  if (r.status === 'running') return 'running';
  const code = r.response?.status_code;
  if (code == null) {
    if (r.status === 'passed') return 'pass';
    if (r.status === 'failed') return 'fail';
    return 'error';
  }

  const isExpectedFailCategory = EXPECTED_FAIL_CATEGORIES.has(
    (r.category || '').toLowerCase(),
  );

  if (isExpectedFailCategory) {
    return code >= 400 ? 'expected_fail' : 'unexpected_pass';
  }
  return code >= 200 && code < 400 ? 'pass' : 'fail';
};

const VERDICT_CONFIG: Record<TestVerdict, { label: string; color: string; shortLabel: string }> = {
  pass:            { label: 'Pass',            color: '#2e7d32', shortLabel: 'PASS' },
  fail:            { label: 'Fail',            color: '#c62828', shortLabel: 'FAIL' },
  expected_fail:   { label: 'Expected Fail',   color: '#ed6c02', shortLabel: 'EXPECTED' },
  unexpected_pass: { label: 'Unexpected Pass', color: '#9c27b0', shortLabel: 'UNEXP PASS' },
  running:         { label: 'Running',         color: '#1565c0', shortLabel: '...' },
  error:           { label: 'Error',           color: '#c62828', shortLabel: 'ERR' },
};

const MethodBadge: React.FC<{ method: string }> = ({ method }) => (
  <Typography component="span" sx={{
    fontSize: '0.6rem', fontWeight: 700, color: '#fff', px: 0.5, py: 0.1,
    borderRadius: 0.5, bgcolor: METHOD_COLORS[method] || '#757575',
    fontFamily: 'monospace', lineHeight: 1.4, display: 'inline-block',
  }}>
    {method}
  </Typography>
);

// ============================================================================
// DevTools Detail Tab Components
// ============================================================================

const SectionLabel: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Typography variant="caption" fontWeight={700} fontSize="0.65rem" color="text.secondary"
    sx={{ display: 'block', mt: 0.75, mb: 0.25 }}>
    {children}
  </Typography>
);

const KeyValueList: React.FC<{ entries: [string, any][] }> = ({ entries }) => (
  <Box sx={{
    mt: 0.25, mb: 1, fontFamily: 'monospace', fontSize: '0.63rem',
    '& .kv-row': {
      display: 'flex', py: 0.15,
      borderBottom: '1px solid', borderColor: 'divider',
      '&:last-child': { borderBottom: 'none' },
    },
    '& .kv-key': { color: 'text.secondary', minWidth: 140, fontWeight: 600, mr: 1 },
    '& .kv-val': { flex: 1, wordBreak: 'break-all' },
  }}>
    {entries.filter(([, v]) => v != null && v !== '').map(([key, val]) => (
      <Box key={key} className="kv-row">
        <Box className="kv-key">{key}:</Box>
        <Box className="kv-val">{String(val)}</Box>
      </Box>
    ))}
  </Box>
);

const HeadersTab: React.FC<{ step: APIStepResult }> = ({ step }) => {
  const queryParams = parseQueryParams(step.request?.url);
  const statusCode = step.response?.status_code;
  const statusText = statusCode
    ? `${statusCode} ${STATUS_REASONS[statusCode] || ''}`.trim()
    : undefined;
  const statusDetail = statusCode ? STATUS_DETAIL[statusCode] : undefined;

  return (
    <Box>
      {/* General */}
      <SectionLabel>General</SectionLabel>
      <KeyValueList entries={[
        ['Request URL', step.request?.url],
        ['Request Method', step.request?.method],
        ['Status Code', statusDetail ? `${statusText} — ${statusDetail}` : statusText],
        ['Content-Type', step.response?.content_type],
        ['Remote Address', (step as any).remote_address],
      ]} />

      {/* Query String Parameters */}
      {queryParams.length > 0 && (
        <>
          <SectionLabel>Query String Parameters</SectionLabel>
          <KeyValueList entries={queryParams} />
        </>
      )}

      {/* Response Headers */}
      {step.response?.headers && Object.keys(step.response.headers).length > 0 && (
        <>
          <SectionLabel>Response Headers ({Object.keys(step.response.headers).length})</SectionLabel>
          <KeyValueList entries={Object.entries(step.response.headers)} />
        </>
      )}

      {/* Request Headers */}
      {step.request?.headers && Object.keys(step.request.headers).length > 0 && (
        <>
          <SectionLabel>Request Headers ({Object.keys(step.request.headers).length})</SectionLabel>
          <KeyValueList entries={Object.entries(step.request.headers)} />
        </>
      )}
    </Box>
  );
};

const PayloadTab: React.FC<{ step: APIStepResult }> = ({ step }) => {
  const bodyStr = step.request?.body
    ? typeof step.request.body === 'string'
      ? step.request.body
      : JSON.stringify(step.request.body, null, 2)
    : null;
  const size = step.request?.body ? estimateSize(step.request.body) : '';

  return (
    <Box>
      {bodyStr ? (
        <>
          {size && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5, fontSize: '0.6rem' }}>
              Size: {size}
            </Typography>
          )}
          <Box sx={{
            fontFamily: 'monospace', fontSize: '0.65rem', whiteSpace: 'pre-wrap',
            wordBreak: 'break-all', p: 0.75, bgcolor: 'background.paper',
            borderRadius: 0.5, border: '1px solid', borderColor: 'divider',
            maxHeight: 400, overflow: 'auto',
          }}>
            {bodyStr}
          </Box>
        </>
      ) : (
        <Typography variant="caption" color="text.secondary">No request payload</Typography>
      )}
    </Box>
  );
};

const ResponseTab: React.FC<{ step: APIStepResult }> = ({ step }) => {
  const bodyStr = step.response?.body
    ? typeof step.response.body === 'string'
      ? step.response.body
      : JSON.stringify(step.response.body, null, 2)
    : null;
  const size = step.response?.body ? estimateSize(step.response.body) : '';

  return (
    <Box>
      {bodyStr ? (
        <>
          {size && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5, fontSize: '0.6rem' }}>
              Size: {size}
            </Typography>
          )}
          <Box sx={{
            fontFamily: 'monospace', fontSize: '0.65rem', whiteSpace: 'pre-wrap',
            wordBreak: 'break-all', p: 0.75, bgcolor: 'background.paper',
            borderRadius: 0.5, border: '1px solid', borderColor: 'divider',
            maxHeight: 400, overflow: 'auto',
          }}>
            {bodyStr}
          </Box>
        </>
      ) : (
        <Typography variant="caption" color="text.secondary">No response body</Typography>
      )}
    </Box>
  );
};

const TimingTab: React.FC<{ step: APIStepResult }> = ({ step }) => {
  const stepDuration = step.duration_ms;
  const responseDuration = step.response?.duration_ms;
  const duration = stepDuration || responseDuration || 0;

  return (
    <Box>
      {stepDuration != null && responseDuration != null && stepDuration !== responseDuration ? (
        <>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
            <Typography variant="caption" fontWeight={600} sx={{ minWidth: 110 }}>Server Time:</Typography>
            <Typography variant="caption" fontFamily="monospace">{responseDuration}ms</Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <Typography variant="caption" fontWeight={600} sx={{ minWidth: 110 }}>Total (incl. overhead):</Typography>
            <Typography variant="caption" fontFamily="monospace">{stepDuration}ms</Typography>
          </Box>
        </>
      ) : (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          <Typography variant="caption" fontWeight={600}>Total Duration:</Typography>
          <Typography variant="caption" fontFamily="monospace">{duration}ms</Typography>
        </Box>
      )}
      <LinearProgress
        variant="determinate"
        value={Math.min(100, (duration / 2000) * 100)}
        sx={{ height: 8, borderRadius: 4 }}
        color={duration > 1000 ? 'error' : duration > 500 ? 'warning' : 'success'}
      />
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5 }}>
        <Typography variant="caption" color="text.secondary">
          {duration < 200 ? 'Fast' : duration < 500 ? 'Normal' : duration < 1000 ? 'Slow' : 'Very Slow'}
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          {[200, 500, 1000].map(t => (
            <Typography key={t} variant="caption" sx={{
              fontSize: '0.55rem',
              color: duration > t ? 'error.main' : 'text.disabled',
            }}>
              {t}ms
            </Typography>
          ))}
        </Box>
      </Box>
    </Box>
  );
};

// ============================================================================
// Step Row
// ============================================================================

const StepRow: React.FC<{ step: APIStepResult; index: number; isUITest?: boolean }> = ({ step, index, isUITest = false }) => {
  const [expanded, setExpanded] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const statusCode = step.response?.status_code;
  const verdict = getVerdict(step);
  const isSuccess = verdict === 'pass' || verdict === 'expected_fail';
  const isFailed = verdict === 'fail' || verdict === 'error';
  const isExpectedFail = verdict === 'expected_fail';
  const isUnexpectedPass = verdict === 'unexpected_pass';
  const isRunning = step.status === 'running';
  const method = step.request?.method || step.action_id?.replace('api_', '').toUpperCase() || '?';
  const path = step.request?.url?.replace(/^https?:\/\/[^/]+/, '') || '';
  const fullUrl = step.request?.url || '';
  // For UI tests, show test name from description (nodeid)
  const testName = step.description?.includes('::')
    ? step.description.split('::').pop() || step.description
    : step.description || '';

  return (
    <Box sx={{
      borderBottom: '1px solid', borderColor: 'divider',
      '&:last-child': { borderBottom: 'none' },
    }}>
      {/* Row header */}
      <Box
        onClick={() => !isRunning && setExpanded(!expanded)}
        title={isUITest ? (step.description || '') : fullUrl}
        sx={{
          display: 'flex', alignItems: 'center', gap: 0.5,
          px: 1, py: 0.5, cursor: isRunning ? 'default' : 'pointer',
          '&:hover': { bgcolor: 'action.hover' },
          bgcolor: isFailed ? 'error.50' : isExpectedFail ? '#fff8e1' : isUnexpectedPass ? '#f3e5f5' : undefined,
        }}
      >
        {/* Status icon */}
        {isRunning ? (
          <RunningIcon sx={{ fontSize: '0.85rem', color: 'info.main', animation: 'spin 1.5s linear infinite', '@keyframes spin': { '0%': { transform: 'rotate(0deg)' }, '100%': { transform: 'rotate(360deg)' } } }} />
        ) : isExpectedFail ? (
          <PassedIcon sx={{ fontSize: '0.85rem', color: 'warning.main' }} />
        ) : isUnexpectedPass ? (
          <FailedIcon sx={{ fontSize: '0.85rem', color: '#9c27b0' }} />
        ) : isSuccess ? (
          <PassedIcon sx={{ fontSize: '0.85rem', color: 'success.main' }} />
        ) : (
          <FailedIcon sx={{ fontSize: '0.85rem', color: 'error.main' }} />
        )}

        {/* Step number */}
        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem', minWidth: 20 }}>
          {index + 1}.
        </Typography>

        {/* Method badge (API tests only) or verdict chip (UI tests) */}
        {isUITest ? (
          <Chip
            label={isSuccess ? 'PASS' : 'FAIL'}
            size="small"
            variant="filled"
            sx={{
              height: 16, fontSize: '0.55rem', fontWeight: 700,
              bgcolor: isSuccess ? '#2e7d32' : '#c62828', color: '#fff',
              minWidth: 36,
            }}
          />
        ) : (
          <MethodBadge method={method} />
        )}

        {/* Test name (UI) or Path (API) */}
        <Typography variant="caption" sx={{
          fontFamily: 'monospace', fontSize: '0.75rem', flex: 1, overflow: 'hidden',
          textOverflow: 'ellipsis', whiteSpace: 'nowrap',
        }}>
          {isUITest ? testName : path}
        </Typography>

        {/* Status code + verdict (API tests only) */}
        {!isUITest && statusCode && (
          <Chip label={statusCode} size="small"
            color={isSuccess ? 'success' : isExpectedFail ? 'warning' : 'error'} variant="outlined"
            sx={{ height: 16, fontSize: '0.6rem', minWidth: 32 }} />
        )}
        {isExpectedFail && (
          <Chip label="expected" size="small" variant="filled"
            sx={{ height: 14, fontSize: '0.5rem', bgcolor: '#ed6c02', color: '#fff' }} />
        )}
        {isUnexpectedPass && (
          <Chip label="unexpected" size="small" variant="filled"
            sx={{ height: 14, fontSize: '0.5rem', bgcolor: '#9c27b0', color: '#fff' }} />
        )}

        {/* Duration */}
        {step.duration_ms != null && (
          <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.6rem', minWidth: 35, textAlign: 'right' }}>
            {step.duration_ms}ms
          </Typography>
        )}

        {/* Category chip */}
        {step.category && (
          <Chip label={step.category} size="small" variant="outlined"
            sx={{ height: 14, fontSize: '0.55rem', textTransform: 'capitalize' }} />
        )}

        {/* Expand arrow */}
        {!isRunning && (
          <ExpandMoreIcon sx={{
            fontSize: '0.8rem', color: 'text.disabled',
            transform: expanded ? 'rotate(180deg)' : 'none',
            transition: 'transform 0.2s',
          }} />
        )}
      </Box>

      {/* Expanded details — DevTools-style tabs */}
      <Collapse in={expanded || (isFailed && !isRunning)}>
        <Box sx={{ px: 1, py: 0.5, bgcolor: isFailed ? 'error.50' : isExpectedFail ? '#fff8e1' : 'grey.50' }}>
          {/* Description & error alerts above tabs */}
          {step.description && (
            <Typography variant="caption" sx={{ display: 'block', fontSize: '0.72rem', mb: 0.5, color: 'text.secondary' }}>
              {step.description}
            </Typography>
          )}
          {isExpectedFail && statusCode && statusCode >= 400 && (
            <Alert severity="warning" variant="outlined"
              sx={{ py: 0, px: 0.75, mb: 0.5, fontSize: '0.7rem', '& .MuiAlert-message': { py: 0.25 } }}>
              {statusCode} {STATUS_REASONS[statusCode] || ''} — Expected for {step.category} test
            </Alert>
          )}
          {isUnexpectedPass && statusCode && statusCode >= 200 && statusCode < 300 && (
            <Alert severity="warning" variant="outlined"
              sx={{ py: 0, px: 0.75, mb: 0.5, fontSize: '0.7rem', '& .MuiAlert-message': { py: 0.25 } }}>
              {statusCode} {STATUS_REASONS[statusCode] || ''} — Unexpected: {step.category} test should have been rejected
            </Alert>
          )}
          {isFailed && statusCode && STATUS_REASONS[statusCode] && statusCode >= 400 && (
            <Alert severity="error" variant="outlined"
              sx={{ py: 0, px: 0.75, mb: 0.5, fontSize: '0.7rem', '& .MuiAlert-message': { py: 0.25 } }}>
              {statusCode} {STATUS_REASONS[statusCode]}
              {STATUS_DETAIL[statusCode] ? ` — ${STATUS_DETAIL[statusCode]}` : ''}
            </Alert>
          )}
          {step.error && !(statusCode && statusCode >= 400 && STATUS_REASONS[statusCode]) && (
            <Alert severity="error" variant="outlined"
              sx={{ py: 0, px: 0.75, mb: 0.5, fontSize: '0.7rem', '& .MuiAlert-message': { py: 0.25 } }}>
              {typeof step.error === 'string' ? step.error : JSON.stringify(step.error)}
            </Alert>
          )}

          {/* DevTools-style tabs (API tests) or simple detail (UI tests) */}
          {!isUITest && (
            <>
              <Tabs
                value={activeTab}
                onChange={(_, v) => setActiveTab(v)}
                sx={{
                  minHeight: 28,
                  '& .MuiTab-root': {
                    minHeight: 28, fontSize: '0.65rem', py: 0,
                    textTransform: 'none', minWidth: 'auto', px: 1.5,
                  },
                  '& .MuiTabs-indicator': { height: 2 },
                }}
              >
                <Tab label="Headers" />
                <Tab label="Payload" />
                <Tab label="Response" />
                <Tab label="Timing" />
              </Tabs>
              <Box sx={{ pt: 0.5 }}>
                {activeTab === 0 && <HeadersTab step={step} />}
                {activeTab === 1 && <PayloadTab step={step} />}
                {activeTab === 2 && <ResponseTab step={step} />}
                {activeTab === 3 && <TimingTab step={step} />}
              </Box>
            </>
          )}

          {/* Security vulnerabilities remain at bottom */}
          {step.security && step.security.vulnerabilities.length > 0 && (
            <Box sx={{ mb: 0.5, mt: 0.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.25 }}>
                <SecurityIcon sx={{ fontSize: '0.7rem', color: 'warning.main' }} />
                <Typography variant="caption" fontWeight={600} fontSize="0.65rem" color="warning.main">
                  {step.security.vulnerabilities.length} Vulnerabilities Found
                </Typography>
              </Box>
              {step.security.vulnerabilities.map((v, i) => (
                <Box key={i} sx={{ ml: 1, display: 'flex', gap: 0.5, alignItems: 'center', py: 0.1 }}>
                  <Chip label={v.severity} size="small"
                    color={v.severity === 'critical' || v.severity === 'high' ? 'error' : 'warning'}
                    sx={{ height: 14, fontSize: '0.55rem' }} />
                  <Typography variant="caption" fontSize="0.6rem">{v.type}: {v.evidence}</Typography>
                </Box>
              ))}
            </Box>
          )}
        </Box>
      </Collapse>
    </Box>
  );
};

// ============================================================================
// Summary View
// ============================================================================

interface StatusGroup {
  label: string;
  color: string;
  count: number;
}

const StatCard: React.FC<{
  label: string; value: number; total: number; color: string;
  active?: boolean; onClick?: () => void;
}> = ({ label, value, total, color, active, onClick }) => {
  const pct = total > 0 ? ((value / total) * 100).toFixed(0) : '0';
  return (
    <Box
      onClick={onClick}
      sx={{
        flex: 1, minWidth: 60, p: 0.75, borderRadius: 1,
        border: '2px solid', textAlign: 'center',
        borderColor: active ? color : 'divider',
        bgcolor: active ? `${color}08` : undefined,
        cursor: onClick ? 'pointer' : 'default',
        transition: 'all 0.15s ease',
        '&:hover': onClick ? { borderColor: color, bgcolor: `${color}08` } : {},
      }}
    >
      <Typography variant="h6" fontWeight={700} fontSize="1.1rem" sx={{ color }}>
        {value}
      </Typography>
      <Typography variant="caption" color="text.secondary" fontSize="0.6rem" display="block">
        {label}
      </Typography>
      {total > 0 && (
        <Typography variant="caption" fontSize="0.55rem" sx={{ color }}>
          {pct}%
        </Typography>
      )}
    </Box>
  );
};

/** Derive a display label for a result's status (e.g. "200", "404", "ERR") */
const statusLabel = (r: APIStepResult): string => {
  const c = r.response?.status_code;
  if (c != null) return String(c);
  if (r.status === 'running') return 'running';
  return 'ERR';
};

/** Format filtered rows as tab-separated text for pasting into chat */
const formatRowsForCopy = (rows: APIStepResult[]): string => {
  const header = '#\tMethod\tPath\tStatus\tVerdict\tDuration\tCategory';
  const lines = rows.map((r, i) => {
    const method = r.request?.method || '?';
    const path = r.request?.url?.replace(/^https?:\/\/[^/]+/, '') || '';
    const status = statusLabel(r);
    const verdict = VERDICT_CONFIG[getVerdict(r)].label;
    const dur = r.duration_ms || r.response?.duration_ms;
    const cat = r.category || '—';
    return `${i + 1}\t${method}\t${path}\t${status}\t${verdict}\t${dur != null ? `${dur}ms` : '—'}\t${cat}`;
  });
  return [header, ...lines].join('\n');
};

type VerdictFilter = 'all' | 'pass' | 'fail' | 'expected_fail' | 'unexpected_pass' | 'running';

type CoverageFilter = 'all' | 'tested' | 'failed' | 'pending';

const SummaryView: React.FC<{ results: APIStepResult[]; discoveredEndpoints?: SlavePreflightEndpoint[]; apiProbeCount?: number; isUITest?: boolean }> = ({ results, discoveredEndpoints, apiProbeCount = 0, isUITest = false }) => {
  const [statusFilter, setStatusFilter] = useState<string | null>(null);
  const [categoryFilter, setCategoryFilter] = useState<string | null>(null);
  const [verdictFilter, setVerdictFilter] = useState<VerdictFilter>('all');
  const [coverageFilter, setCoverageFilter] = useState<CoverageFilter>('all');
  const [copied, setCopied] = useState(false);

  const stats = useMemo(() => {
    // Verdict-aware counts
    const verdicts = results.map(r => getVerdict(r));
    const passCount = verdicts.filter(v => v === 'pass').length;
    const failCount = verdicts.filter(v => v === 'fail').length;
    const expectedFailCount = verdicts.filter(v => v === 'expected_fail').length;
    const unexpectedPassCount = verdicts.filter(v => v === 'unexpected_pass').length;
    const runningCount = verdicts.filter(v => v === 'running').length;
    const errorCount = verdicts.filter(v => v === 'error').length;
    const completed = results.length - runningCount;

    // Status code groups
    const s2xx = results.filter(r => { const c = r.response?.status_code; return c != null && c >= 200 && c < 300; });
    const s3xx = results.filter(r => { const c = r.response?.status_code; return c != null && c >= 300 && c < 400; });
    const s4xx = results.filter(r => { const c = r.response?.status_code; return c != null && c >= 400 && c < 500; });
    const s5xx = results.filter(r => { const c = r.response?.status_code; return c != null && c >= 500; });
    const noResp = results.filter(r => !r.response?.status_code && r.status !== 'running');

    // Categories with verdict-aware pass/fail
    const catMap: Record<string, {
      total: number; passed: number; failed: number;
      expectedFail: number; unexpectedPass: number;
    }> = {};
    results.forEach(r => {
      const cat = r.category || 'uncategorized';
      if (!catMap[cat]) catMap[cat] = { total: 0, passed: 0, failed: 0, expectedFail: 0, unexpectedPass: 0 };
      catMap[cat].total++;
      const v = getVerdict(r);
      if (v === 'pass') catMap[cat].passed++;
      if (v === 'fail' || v === 'error') catMap[cat].failed++;
      if (v === 'expected_fail') catMap[cat].expectedFail++;
      if (v === 'unexpected_pass') catMap[cat].unexpectedPass++;
    });

    // Performance
    const durations = results
      .map(r => r.duration_ms || r.response?.duration_ms)
      .filter((d): d is number => d != null && d > 0);
    const avgDuration = durations.length > 0
      ? Math.round(durations.reduce((a, b) => a + b, 0) / durations.length)
      : 0;
    const withDuration = results
      .filter(r => (r.duration_ms || r.response?.duration_ms || 0) > 0)
      .map(r => ({
        method: r.request?.method || '?',
        path: r.request?.url?.replace(/^https?:\/\/[^/]+/, '') || '',
        duration: r.duration_ms || r.response?.duration_ms || 0,
      }));
    const sorted = [...withDuration].sort((a, b) => b.duration - a.duration);
    const slowest3 = sorted.slice(0, 3);
    const fastest3 = sorted.length > 0 ? [...withDuration].sort((a, b) => a.duration - b.duration).slice(0, 3) : [];

    return {
      total: results.length,
      passed: passCount,
      failed: failCount,
      expectedFail: expectedFailCount,
      unexpectedPass: unexpectedPassCount,
      running: runningCount,
      errors: errorCount,
      completed,
      statusGroups: [
        { label: '2XX Success', color: '#2e7d32', count: s2xx.length },
        { label: '3XX Redirect', color: '#1565c0', count: s3xx.length },
        { label: '4XX Client Error', color: '#e65100', count: s4xx.length },
        { label: '5XX Server Error', color: '#c62828', count: s5xx.length },
        { label: 'No Response', color: '#757575', count: noResp.length },
      ] as StatusGroup[],
      catMap,
      avgDuration,
      slowest3,
      fastest3,
    };
  }, [results]);

  if (results.length === 0) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
        <Typography variant="body2" color="text.secondary">
          No results yet — run an API test to see the summary.
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 1.5, overflow: 'auto', height: '100%' }}>
      {/* Overview Cards */}
      <Box sx={{ display: 'flex', gap: 0.75, mb: 1.5, flexWrap: 'wrap' }}>
        <StatCard label="Total" value={stats.total} total={0} color="text.primary"
          active={verdictFilter === 'all'}
          onClick={() => setVerdictFilter(verdictFilter === 'all' ? 'all' : 'all')} />
        <StatCard label="Passed" value={stats.passed} total={stats.completed} color="#2e7d32"
          active={verdictFilter === 'pass'}
          onClick={() => setVerdictFilter(verdictFilter === 'pass' ? 'all' : 'pass')} />
        <StatCard label="Failed" value={stats.failed + stats.errors} total={stats.completed} color="#c62828"
          active={verdictFilter === 'fail'}
          onClick={() => setVerdictFilter(verdictFilter === 'fail' ? 'all' : 'fail')} />
        {stats.expectedFail > 0 && (
          <StatCard label="Expected Fail" value={stats.expectedFail} total={stats.completed} color="#ed6c02"
            active={verdictFilter === 'expected_fail'}
            onClick={() => setVerdictFilter(verdictFilter === 'expected_fail' ? 'all' : 'expected_fail')} />
        )}
        {stats.unexpectedPass > 0 && (
          <StatCard label="Unexpected Pass" value={stats.unexpectedPass} total={stats.completed} color="#9c27b0"
            active={verdictFilter === 'unexpected_pass'}
            onClick={() => setVerdictFilter(verdictFilter === 'unexpected_pass' ? 'all' : 'unexpected_pass')} />
        )}
        <StatCard label="Running" value={stats.running} total={0} color="#1565c0"
          active={verdictFilter === 'running'}
          onClick={() => setVerdictFilter(verdictFilter === 'running' ? 'all' : 'running')} />
      </Box>

      {/* API Probe count (secondary stat) */}
      {apiProbeCount > 0 && (
        <Typography variant="caption" color="text.secondary" fontSize="0.6rem" sx={{ display: 'block', mb: 1 }}>
          + {apiProbeCount} API probe call{apiProbeCount !== 1 ? 's' : ''} (not included in stats above)
        </Typography>
      )}

      {/* API Endpoint Coverage Matrix */}
      {discoveredEndpoints && discoveredEndpoints.length > 0 && (() => {
        // Collect ALL tested method+path pairs from results, including
        // every http_call (not just the representative one in `request`).
        // Strips protocol+host and query string so only the path remains.
        const toPath = (url: string) =>
          url.replace(/^https?:\/\/[^/]+/, '').split('?')[0];
        const testedPairs: { method: string; path: string; status: string }[] = [];
        results.forEach(r => {
          // From http_calls (most accurate — actual HTTP traffic)
          if (r.http_calls?.length) {
            r.http_calls.forEach((hc: any) => {
              const m = (hc.method || '').toUpperCase();
              const p = toPath(hc.url || '');
              if (m && p) testedPairs.push({ method: m, path: p, status: r.status || 'unknown' });
            });
          }
          // Fallback: use the request object if no http_calls
          else if (r.request?.method && r.request?.url) {
            const m = r.request.method.toUpperCase();
            const p = toPath(r.request.url);
            // Skip if URL is just a function name (no slash = not a real URL)
            if (m && p && p.startsWith('/')) {
              testedPairs.push({ method: m, path: p, status: r.status || 'unknown' });
            }
          }
        });

        // For each discovered endpoint, build a regex from its path pattern.
        // Split on {param} tokens, escape literal segments, rejoin with [^/]+.
        // e.g. /api/admin/students/{student_id} → ^/api/admin/students/[^/]+/?$
        const escapeRe = (s: string) => s.replace(/[-\\^$*+?.()|[\]{}]/g, '\\$&');
        type EndpointStatus = 'tested' | 'failed' | 'pending';
        const classified = discoveredEndpoints.map(ep => {
          const epMethod = ep.method.toUpperCase();
          const segments = ep.path.split(/\{[^}]+\}/);
          const regexStr = '^' + segments.map(escapeRe).join('[^/]+') + '/?$';
          const pathRegex = new RegExp(regexStr);

          let status: EndpointStatus = 'pending';
          for (const tp of testedPairs) {
            if (tp.method === epMethod && pathRegex.test(tp.path)) {
              status = (tp.status === 'passed' || tp.status === 'skipped') ? 'tested' : 'failed';
              // If any test for this endpoint failed, keep it as failed
              if (status === 'failed') break;
            }
          }
          return { ...ep, coverageStatus: status };
        });

        const testedCount = classified.filter(e => e.coverageStatus === 'tested').length;
        const failedCount = classified.filter(e => e.coverageStatus === 'failed').length;
        const pendingCount = classified.filter(e => e.coverageStatus === 'pending').length;
        const totalDisc = classified.length;
        const pct = totalDisc > 0 ? Math.round(((testedCount + failedCount) / totalDisc) * 100) : 0;

        // Filter endpoints based on coverageFilter
        const coverageFiltered = coverageFilter === 'all' ? classified
          : classified.filter(e => e.coverageStatus === coverageFilter);

        // Group by tag
        const byTag: Record<string, typeof classified> = {};
        coverageFiltered.forEach(ep => {
          const tag = ep.tag || 'Other';
          if (!byTag[tag]) byTag[tag] = [];
          byTag[tag].push(ep);
        });
        const tagEntries = Object.entries(byTag).sort(([a], [b]) => a.localeCompare(b));

        return (
          <Box sx={{ mb: 1.5 }}>
            <SectionLabel>API Endpoint Coverage</SectionLabel>

            {/* Progress bar */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
              <LinearProgress
                variant="determinate"
                value={pct}
                sx={{ flex: 1, height: 8, borderRadius: 4 }}
                color={pct >= 80 ? 'success' : pct >= 50 ? 'warning' : 'error'}
              />
              <Typography variant="caption" fontWeight={600} fontSize="0.7rem">
                {testedCount + failedCount}/{totalDisc} ({pct}%)
              </Typography>
            </Box>

            {/* Filter chips — clickable to toggle view */}
            <Box sx={{ display: 'flex', gap: 0.5, mb: 1, flexWrap: 'wrap' }}>
              <Chip
                label={`All (${totalDisc})`} size="small"
                variant={coverageFilter === 'all' ? 'filled' : 'outlined'}
                color={coverageFilter === 'all' ? 'primary' : 'default'}
                onClick={() => setCoverageFilter('all')}
                sx={{ height: 22, fontSize: '0.6rem', fontWeight: 600, cursor: 'pointer' }}
              />
              <Chip
                label={`${testedCount} Tested`} size="small"
                variant={coverageFilter === 'tested' ? 'filled' : 'outlined'}
                onClick={() => setCoverageFilter(coverageFilter === 'tested' ? 'all' : 'tested')}
                sx={{
                  height: 22, fontSize: '0.6rem', fontWeight: 600, cursor: 'pointer',
                  ...(coverageFilter === 'tested'
                    ? { bgcolor: '#2e7d32', color: '#fff' }
                    : { color: '#2e7d32', borderColor: '#2e7d32' }),
                }}
              />
              {failedCount > 0 && (
                <Chip
                  label={`${failedCount} Failed`} size="small"
                  variant={coverageFilter === 'failed' ? 'filled' : 'outlined'}
                  onClick={() => setCoverageFilter(coverageFilter === 'failed' ? 'all' : 'failed')}
                  sx={{
                    height: 22, fontSize: '0.6rem', fontWeight: 600, cursor: 'pointer',
                    ...(coverageFilter === 'failed'
                      ? { bgcolor: '#c62828', color: '#fff' }
                      : { color: '#c62828', borderColor: '#c62828' }),
                  }}
                />
              )}
              <Chip
                label={`${pendingCount} Pending`} size="small"
                variant={coverageFilter === 'pending' ? 'filled' : 'outlined'}
                onClick={() => setCoverageFilter(coverageFilter === 'pending' ? 'all' : 'pending')}
                sx={{
                  height: 22, fontSize: '0.6rem', fontWeight: 600, cursor: 'pointer',
                  ...(coverageFilter === 'pending'
                    ? { bgcolor: '#ff9800', color: '#fff' }
                    : { color: '#e65100', borderColor: '#e65100' }),
                }}
              />
            </Box>

            {/* Endpoint list grouped by tag */}
            <Box sx={{
              maxHeight: 350, overflow: 'auto', border: '1px solid',
              borderColor: 'divider', borderRadius: 1,
            }}>
              {tagEntries.length === 0 ? (
                <Box sx={{ p: 2, textAlign: 'center' }}>
                  <Typography variant="caption" color="text.secondary">
                    No endpoints match the selected filter
                  </Typography>
                </Box>
              ) : tagEntries.map(([tag, endpoints]) => {
                const tagTested = endpoints.filter(e => e.coverageStatus === 'tested').length;
                const tagFailed = endpoints.filter(e => e.coverageStatus === 'failed').length;
                const tagPending = endpoints.filter(e => e.coverageStatus === 'pending').length;
                return (
                  <Box key={tag}>
                    {/* Tag header */}
                    <Box sx={{
                      display: 'flex', alignItems: 'center', gap: 0.5,
                      px: 0.75, py: 0.3, bgcolor: 'grey.100',
                      borderBottom: '1px solid', borderColor: 'divider',
                      position: 'sticky', top: 0, zIndex: 1,
                    }}>
                      <Typography variant="caption" fontWeight={700} fontSize="0.6rem" sx={{ flex: 1 }}>
                        {tag}
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                        {tagTested > 0 && (
                          <Typography variant="caption" fontSize="0.5rem" sx={{ color: '#2e7d32' }}>
                            {tagTested} tested
                          </Typography>
                        )}
                        {tagFailed > 0 && (
                          <Typography variant="caption" fontSize="0.5rem" sx={{ color: '#c62828' }}>
                            {tagFailed} failed
                          </Typography>
                        )}
                        {tagPending > 0 && (
                          <Typography variant="caption" fontSize="0.5rem" sx={{ color: '#e65100' }}>
                            {tagPending} pending
                          </Typography>
                        )}
                      </Box>
                    </Box>
                    {/* Endpoints */}
                    {endpoints.map((ep, i) => (
                      <Box key={i} sx={{
                        display: 'flex', alignItems: 'center', gap: 0.5,
                        px: 0.75, py: 0.25,
                        borderBottom: '1px solid', borderColor: 'divider',
                        bgcolor: ep.coverageStatus === 'failed' ? '#ffebee'
                          : ep.coverageStatus === 'pending' ? '#fff3e0' : undefined,
                      }}>
                        <MethodBadge method={ep.method} />
                        <Typography variant="caption" fontFamily="monospace" fontSize="0.6rem" sx={{
                          flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                        }} title={`${ep.path} — ${ep.description || ''}`}>
                          {ep.path}
                        </Typography>
                        {ep.coverageStatus === 'tested' && (
                          <Chip label="TESTED" size="small" variant="filled"
                            sx={{ height: 16, fontSize: '0.5rem', fontWeight: 700, bgcolor: '#2e7d32', color: '#fff' }} />
                        )}
                        {ep.coverageStatus === 'failed' && (
                          <Chip label="FAILED" size="small" variant="filled"
                            sx={{ height: 16, fontSize: '0.5rem', fontWeight: 700, bgcolor: '#c62828', color: '#fff' }} />
                        )}
                        {ep.coverageStatus === 'pending' && (
                          <Chip label="PENDING" size="small" variant="outlined"
                            sx={{ height: 16, fontSize: '0.5rem', fontWeight: 600, color: '#e65100', borderColor: '#e65100' }} />
                        )}
                      </Box>
                    ))}
                  </Box>
                );
              })}
            </Box>
          </Box>
        );
      })()}

      {/* Verdict explanation note */}
      {(stats.expectedFail > 0 || stats.unexpectedPass > 0) && (
        <Alert severity="info" variant="outlined"
          sx={{ py: 0, px: 0.75, mb: 1.5, fontSize: '0.65rem', '& .MuiAlert-message': { py: 0.25 } }}>
          <strong>Expected Fail</strong> = negative/security tests that correctly received error responses (working as intended).
          {stats.unexpectedPass > 0 && (
            <> <strong>Unexpected Pass</strong> = negative/security tests that received 2XX when they should have been rejected.</>
          )}
        </Alert>
      )}

      {/* Status Code Breakdown */}
      <SectionLabel>Status Code Breakdown</SectionLabel>
      <Box sx={{ display: 'flex', gap: 0.5, mb: 1.5, flexWrap: 'wrap' }}>
        {stats.statusGroups.filter(g => g.count > 0).map(g => (
          <Box key={g.label} sx={{
            display: 'flex', alignItems: 'center', gap: 0.5,
            px: 1, py: 0.5, borderRadius: 1,
            border: '1px solid', borderColor: 'divider', minWidth: 100,
          }}>
            <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: g.color, flexShrink: 0 }} />
            <Box>
              <Typography variant="caption" fontWeight={600} fontSize="0.7rem" display="block">
                {g.count}
              </Typography>
              <Typography variant="caption" color="text.secondary" fontSize="0.55rem">
                {g.label}
                {stats.completed > 0 && ` (${((g.count / stats.completed) * 100).toFixed(0)}%)`}
              </Typography>
            </Box>
          </Box>
        ))}
      </Box>

      {/* Category Breakdown */}
      {Object.keys(stats.catMap).length > 0 && Object.keys(stats.catMap).some(k => k !== 'uncategorized') && (
        <>
          <SectionLabel>Category Breakdown</SectionLabel>
          <Box sx={{ display: 'flex', gap: 0.5, mb: 1.5, flexWrap: 'wrap' }}>
            {Object.entries(stats.catMap).map(([cat, data]) => (
              <Box key={cat} sx={{
                px: 1, py: 0.5, borderRadius: 1,
                border: '1px solid', borderColor: 'divider',
              }}>
                <Typography variant="caption" fontWeight={600} fontSize="0.65rem" display="block"
                  sx={{ textTransform: 'capitalize' }}>
                  {cat}
                </Typography>
                <Box sx={{ display: 'flex', gap: 0.75, mt: 0.25, flexWrap: 'wrap' }}>
                  <Typography variant="caption" fontSize="0.6rem" color="text.secondary">
                    Total: {data.total}
                  </Typography>
                  <Typography variant="caption" fontSize="0.6rem" sx={{ color: '#2e7d32' }}>
                    Pass: {data.passed}
                  </Typography>
                  <Typography variant="caption" fontSize="0.6rem" sx={{ color: '#c62828' }}>
                    Fail: {data.failed}
                  </Typography>
                  {data.expectedFail > 0 && (
                    <Typography variant="caption" fontSize="0.6rem" sx={{ color: '#ed6c02' }}>
                      Expected: {data.expectedFail}
                    </Typography>
                  )}
                  {data.unexpectedPass > 0 && (
                    <Typography variant="caption" fontSize="0.6rem" sx={{ color: '#9c27b0' }}>
                      Unexpected: {data.unexpectedPass}
                    </Typography>
                  )}
                </Box>
              </Box>
            ))}
          </Box>
        </>
      )}

      {/* Performance Overview */}
      {stats.avgDuration > 0 && (
        <>
          <SectionLabel>Performance Overview</SectionLabel>
          <Box sx={{ mb: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
              <SpeedIcon sx={{ fontSize: '0.8rem', color: 'text.secondary' }} />
              <Typography variant="caption" fontSize="0.7rem">
                Avg response: <strong>{stats.avgDuration}ms</strong>
              </Typography>
            </Box>
            {stats.slowest3.length > 0 && (
              <Box sx={{ ml: 0.5, mb: 0.5 }}>
                <Typography variant="caption" fontWeight={600} fontSize="0.6rem" color="error.main"
                  sx={{ display: 'block', mb: 0.25 }}>
                  Slowest
                </Typography>
                {stats.slowest3.map((s, i) => (
                  <Box key={i} sx={{ display: 'flex', gap: 0.5, alignItems: 'center', py: 0.1 }}>
                    <MethodBadge method={s.method} />
                    <Typography variant="caption" fontFamily="monospace" fontSize="0.6rem" sx={{
                      flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                    }}>
                      {s.path}
                    </Typography>
                    <Typography variant="caption" fontFamily="monospace" fontSize="0.6rem" color="error.main">
                      {s.duration}ms
                    </Typography>
                  </Box>
                ))}
              </Box>
            )}
            {stats.fastest3.length > 0 && (
              <Box sx={{ ml: 0.5 }}>
                <Typography variant="caption" fontWeight={600} fontSize="0.6rem" color="success.main"
                  sx={{ display: 'block', mb: 0.25 }}>
                  Fastest
                </Typography>
                {stats.fastest3.map((s, i) => (
                  <Box key={i} sx={{ display: 'flex', gap: 0.5, alignItems: 'center', py: 0.1 }}>
                    <MethodBadge method={s.method} />
                    <Typography variant="caption" fontFamily="monospace" fontSize="0.6rem" sx={{
                      flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                    }}>
                      {s.path}
                    </Typography>
                    <Typography variant="caption" fontFamily="monospace" fontSize="0.6rem" color="success.main">
                      {s.duration}ms
                    </Typography>
                  </Box>
                ))}
              </Box>
            )}
          </Box>
        </>
      )}

      {/* API List Table — with filters and copy */}
      {(() => {
        // Unique status labels and categories from data
        const uniqueStatuses = Array.from(new Set(results.map(statusLabel))).sort();
        const uniqueCategories = Array.from(
          new Set(results.map(r => r.category || 'uncategorized'))
        ).sort();

        // Apply filters
        const filtered = results.filter(r => {
          if (statusFilter && statusLabel(r) !== statusFilter) return false;
          if (categoryFilter && (r.category || 'uncategorized') !== categoryFilter) return false;
          if (verdictFilter !== 'all') {
            const v = getVerdict(r);
            if (verdictFilter === 'fail') {
              if (v !== 'fail' && v !== 'error') return false;
            } else if (v !== verdictFilter) {
              return false;
            }
          }
          return true;
        });

        const handleCopy = () => {
          navigator.clipboard.writeText(formatRowsForCopy(filtered)).then(() => {
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
          });
        };

        return (
          <>
            {/* Title row with copy button */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.75, mb: 0.5 }}>
              <Typography variant="caption" fontWeight={700} fontSize="0.65rem" color="text.secondary">
                All APIs ({filtered.length}{filtered.length !== results.length ? ` of ${results.length}` : ''})
              </Typography>
              <Box sx={{ ml: 'auto' }}>
                <Tooltip title={copied ? 'Copied!' : 'Copy filtered list to clipboard'} arrow>
                  <IconButton size="small" onClick={handleCopy} sx={{ p: 0.25 }}>
                    {copied
                      ? <CheckIcon sx={{ fontSize: '0.85rem', color: 'success.main' }} />
                      : <CopyIcon sx={{ fontSize: '0.85rem', color: 'text.secondary' }} />}
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>

            {/* Filter chips row */}
            <Box sx={{ display: 'flex', gap: 0.5, mb: 0.75, flexWrap: 'wrap', alignItems: 'center' }}>
              <FilterIcon sx={{ fontSize: '0.75rem', color: 'text.disabled' }} />

              {/* Status filter chips */}
              <Chip
                label="All Status"
                size="small"
                variant={statusFilter === null ? 'filled' : 'outlined'}
                color={statusFilter === null ? 'primary' : 'default'}
                onClick={() => setStatusFilter(null)}
                sx={{ height: 20, fontSize: '0.55rem' }}
              />
              {uniqueStatuses.map(s => {
                const num = parseInt(s, 10);
                const chipColor = !isNaN(num) && num >= 200 && num < 300
                  ? 'success' as const
                  : !isNaN(num) && num >= 400
                    ? 'error' as const
                    : 'default' as const;
                return (
                  <Chip
                    key={`s-${s}`}
                    label={s}
                    size="small"
                    variant={statusFilter === s ? 'filled' : 'outlined'}
                    color={statusFilter === s ? chipColor : 'default'}
                    onClick={() => setStatusFilter(statusFilter === s ? null : s)}
                    sx={{ height: 20, fontSize: '0.55rem' }}
                  />
                );
              })}

              {/* Divider */}
              <Box sx={{ width: '1px', height: 16, bgcolor: 'divider', mx: 0.25 }} />

              {/* Category filter chips */}
              <Chip
                label="All Categories"
                size="small"
                variant={categoryFilter === null ? 'filled' : 'outlined'}
                color={categoryFilter === null ? 'primary' : 'default'}
                onClick={() => setCategoryFilter(null)}
                sx={{ height: 20, fontSize: '0.55rem', textTransform: 'capitalize' }}
              />
              {uniqueCategories.map(c => (
                <Chip
                  key={`c-${c}`}
                  label={c}
                  size="small"
                  variant={categoryFilter === c ? 'filled' : 'outlined'}
                  color={categoryFilter === c ? 'primary' : 'default'}
                  onClick={() => setCategoryFilter(categoryFilter === c ? null : c)}
                  sx={{ height: 20, fontSize: '0.55rem', textTransform: 'capitalize' }}
                />
              ))}
            </Box>

            {/* Table */}
            <TableContainer sx={{ maxHeight: 300, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    {(isUITest
                      ? ['#', 'Test Name', 'Verdict', 'Duration', 'Category']
                      : ['#', 'Method', 'Path', 'Status', 'Verdict', 'Duration', 'Category']
                    ).map(h => (
                      <TableCell key={h} sx={{
                        py: 0.25, px: 0.5, fontSize: '0.6rem', fontWeight: 700,
                        bgcolor: 'grey.100', lineHeight: 1.4,
                      }}>
                        {h}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filtered.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={isUITest ? 5 : 7} sx={{ textAlign: 'center', py: 2 }}>
                        <Typography variant="caption" color="text.secondary">
                          No tests match the selected filters
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    filtered.map((r, i) => {
                      const code = r.response?.status_code;
                      const v = getVerdict(r);
                      const vc = VERDICT_CONFIG[v];
                      const method = r.request?.method || '?';
                      const path = r.request?.url?.replace(/^https?:\/\/[^/]+/, '') || '';
                      const tName = r.description?.includes('::')
                        ? r.description.split('::').pop() || r.description
                        : r.description || '';
                      const dur = r.duration_ms || r.response?.duration_ms;
                      const rowBg = v === 'fail' || v === 'error'
                        ? 'error.50'
                        : v === 'expected_fail'
                          ? '#fff8e1'
                          : v === 'unexpected_pass'
                            ? '#f3e5f5'
                            : undefined;
                      return (
                        <TableRow key={r.step_index} sx={{
                          bgcolor: rowBg,
                          '&:hover': { bgcolor: 'action.hover' },
                        }}>
                          <TableCell sx={{ py: 0.2, px: 0.5, fontSize: '0.6rem', color: 'text.secondary' }}>
                            {i + 1}
                          </TableCell>
                          {isUITest ? (
                            <TableCell sx={{
                              py: 0.2, px: 0.5, fontFamily: 'monospace', fontSize: '0.6rem',
                              maxWidth: 240, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                            }} title={r.description}>
                              {tName}
                            </TableCell>
                          ) : (
                            <>
                              <TableCell sx={{ py: 0.2, px: 0.5 }}>
                                <MethodBadge method={method} />
                              </TableCell>
                              <TableCell sx={{
                                py: 0.2, px: 0.5, fontFamily: 'monospace', fontSize: '0.6rem',
                                maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                              }} title={r.request?.url}>
                                {path}
                              </TableCell>
                              <TableCell sx={{ py: 0.2, px: 0.5 }}>
                                {code ? (
                                  <Chip label={code} size="small"
                                    color={v === 'pass' ? 'success' : v === 'expected_fail' ? 'warning' : 'error'}
                                    variant="outlined"
                                    sx={{ height: 16, fontSize: '0.55rem' }} />
                                ) : v === 'running' ? (
                                  <Chip label="..." size="small" color="info" variant="outlined"
                                    sx={{ height: 16, fontSize: '0.55rem' }} />
                                ) : (
                                  <Chip label="ERR" size="small" color="error" variant="outlined"
                                    sx={{ height: 16, fontSize: '0.55rem' }} />
                                )}
                              </TableCell>
                            </>
                          )}
                          <TableCell sx={{ py: 0.2, px: 0.5 }}>
                            <Chip label={vc.shortLabel} size="small" variant="filled"
                              sx={{
                                height: 16, fontSize: '0.5rem', fontWeight: 700,
                                bgcolor: vc.color, color: '#fff',
                              }} />
                          </TableCell>
                          <TableCell sx={{ py: 0.2, px: 0.5, fontSize: '0.6rem', fontFamily: 'monospace', color: 'text.secondary' }}>
                            {dur != null ? `${dur}ms` : '—'}
                          </TableCell>
                          <TableCell sx={{ py: 0.2, px: 0.5, fontSize: '0.55rem', textTransform: 'capitalize', color: 'text.secondary' }}>
                            {r.category || '—'}
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </>
        );
      })()}
    </Box>
  );
};

// ============================================================================
// Test Results View (Tab 1) — per-test pass/fail grouped by file
// ============================================================================

type TestResultFilter = 'all' | 'passed' | 'failed';

/** Parse the test file name from a pytest nodeid (e.g. "tests/test_auth.py::test_login" → "test_auth.py") */
const parseTestFile = (nodeid: string): string => {
  // Extract file part before "::" (if present)
  const beforeSep = nodeid.split('::')[0];
  // Get just the filename
  const parts = beforeSep.split('/');
  const fileName = parts[parts.length - 1];
  // If it looks like a file (has extension), use it; otherwise use the full nodeid as group
  return fileName && fileName.includes('.') ? fileName : 'Tests';
};

/** Parse the test function name from a pytest nodeid */
const parseTestName = (nodeid: string): string => {
  const parts = nodeid.split('::');
  return parts.length > 1 ? parts[parts.length - 1] : nodeid;
};

interface TestFileGroup {
  file: string;
  tests: APIStepResult[];
  passed: number;
  failed: number;
}

const TestResultsView: React.FC<{ results: APIStepResult[] }> = ({ results }) => {
  const [filter, setFilter] = useState<TestResultFilter>('all');
  const [expandedRows, setExpandedRows] = useState<Set<number>>(new Set());

  // Filter to only test execution results (exclude API probes)
  const testResults = useMemo(() =>
    results.filter(r => r.source === 'test_execution'),
    [results]
  );

  // Compute stats from test results only
  const stats = useMemo(() => {
    const passCount = testResults.filter(r => r.status === 'passed').length;
    const failCount = testResults.length - passCount;
    return { total: testResults.length, passed: passCount, failed: failCount };
  }, [testResults]);

  // Apply filter then group by file
  const fileGroups: TestFileGroup[] = useMemo(() => {
    let filtered = testResults;
    if (filter === 'passed') filtered = testResults.filter(r => r.status === 'passed');
    else if (filter === 'failed') filtered = testResults.filter(r => r.status !== 'passed');

    const groupMap: Record<string, APIStepResult[]> = {};
    filtered.forEach(r => {
      const file = parseTestFile(r.description || '');
      if (!groupMap[file]) groupMap[file] = [];
      groupMap[file].push(r);
    });

    return Object.entries(groupMap)
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([file, tests]) => ({
        file,
        tests,
        passed: tests.filter(t => t.status === 'passed').length,
        failed: tests.filter(t => t.status !== 'passed').length,
      }));
  }, [testResults, filter]);

  if (testResults.length === 0) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
        <Typography variant="body2" color="text.secondary">
          No test results yet.
        </Typography>
      </Box>
    );
  }

  const toggleRow = (idx: number) => {
    setExpandedRows(prev => {
      const next = new Set(prev);
      if (next.has(idx)) next.delete(idx); else next.add(idx);
      return next;
    });
  };

  return (
    <Box sx={{ p: 1.5, overflow: 'auto', height: '100%' }}>
      {/* Summary header */}
      <Box sx={{ display: 'flex', gap: 1, mb: 1.5 }}>
        <Box sx={{
          flex: 1, p: 1, borderRadius: 1, textAlign: 'center',
          border: '2px solid', borderColor: filter === 'all' ? 'primary.main' : 'divider',
          bgcolor: filter === 'all' ? 'primary.50' : undefined,
          cursor: 'pointer', transition: 'all 0.15s',
          '&:hover': { borderColor: 'primary.main' },
        }} onClick={() => setFilter('all')}>
          <Typography variant="h5" fontWeight={700} fontSize="1.3rem">
            {stats.total}
          </Typography>
          <Typography variant="caption" color="text.secondary" fontSize="0.65rem">
            Total Tests
          </Typography>
        </Box>
        <Box sx={{
          flex: 1, p: 1, borderRadius: 1, textAlign: 'center',
          border: '2px solid', borderColor: filter === 'passed' ? '#2e7d32' : 'divider',
          bgcolor: filter === 'passed' ? '#e8f5e9' : undefined,
          cursor: 'pointer', transition: 'all 0.15s',
          '&:hover': { borderColor: '#2e7d32' },
        }} onClick={() => setFilter(filter === 'passed' ? 'all' : 'passed')}>
          <Typography variant="h5" fontWeight={700} fontSize="1.3rem" sx={{ color: '#2e7d32' }}>
            {stats.passed}
          </Typography>
          <Typography variant="caption" color="text.secondary" fontSize="0.65rem">
            Passed
          </Typography>
        </Box>
        <Box sx={{
          flex: 1, p: 1, borderRadius: 1, textAlign: 'center',
          border: '2px solid', borderColor: filter === 'failed' ? '#c62828' : 'divider',
          bgcolor: filter === 'failed' ? '#ffebee' : undefined,
          cursor: 'pointer', transition: 'all 0.15s',
          '&:hover': { borderColor: '#c62828' },
        }} onClick={() => setFilter(filter === 'failed' ? 'all' : 'failed')}>
          <Typography variant="h5" fontWeight={700} fontSize="1.3rem" sx={{ color: '#c62828' }}>
            {stats.failed}
          </Typography>
          <Typography variant="caption" color="text.secondary" fontSize="0.65rem">
            Failed
          </Typography>
        </Box>
      </Box>

      {/* File-grouped test results */}
      {fileGroups.map((group) => (
        <Box key={group.file} sx={{ mb: 1.5 }}>
          {/* File header */}
          <Box sx={{
            display: 'flex', alignItems: 'center', gap: 0.5,
            px: 0.75, py: 0.4, bgcolor: 'grey.100',
            borderRadius: '4px 4px 0 0', border: '1px solid', borderColor: 'divider',
          }}>
            <ScienceIcon sx={{ fontSize: '0.75rem', color: 'text.secondary' }} />
            <Typography variant="caption" fontWeight={700} fontSize="0.65rem" sx={{ flex: 1 }}>
              {group.file}
            </Typography>
            <Typography variant="caption" fontSize="0.55rem" sx={{ color: '#2e7d32' }}>
              {group.passed} passed
            </Typography>
            {group.failed > 0 && (
              <Typography variant="caption" fontSize="0.55rem" sx={{ color: '#c62828' }}>
                {group.failed} failed
              </Typography>
            )}
          </Box>

          {/* Tests in this file */}
          <TableContainer sx={{ border: '1px solid', borderTop: 'none', borderColor: 'divider', borderRadius: '0 0 4px 4px' }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  {['#', 'Test Name', 'Status', 'Duration', 'HTTP Calls'].map(h => (
                    <TableCell key={h} sx={{
                      py: 0.3, px: 0.75, fontSize: '0.6rem', fontWeight: 700,
                      bgcolor: 'grey.50', lineHeight: 1.4,
                    }}>
                      {h}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {group.tests.map((t, i) => {
                  const isPassed = t.status === 'passed';
                  const testName = parseTestName(t.description || `Test ${i + 1}`);
                  const dur = t.duration_ms || t.response?.duration_ms;
                  const hasExpandable = (t.error && !isPassed) || (t.http_calls && t.http_calls.length > 0);
                  const isExpanded = expandedRows.has(t.step_index);
                  const httpCallCount = t.http_calls?.length || (t.request ? 1 : 0);
                  return (
                    <React.Fragment key={t.step_index ?? i}>
                      <TableRow
                        onClick={() => hasExpandable && toggleRow(t.step_index)}
                        sx={{
                          bgcolor: !isPassed ? '#ffebee' : undefined,
                          cursor: hasExpandable ? 'pointer' : 'default',
                          '&:hover': { bgcolor: !isPassed ? '#ffcdd2' : 'action.hover' },
                        }}
                      >
                        <TableCell sx={{ py: 0.3, px: 0.75, fontSize: '0.6rem', color: 'text.secondary', width: 30 }}>
                          {i + 1}
                        </TableCell>
                        <TableCell sx={{
                          py: 0.3, px: 0.75, fontSize: '0.65rem', fontFamily: 'monospace',
                          maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                        }} title={t.description || testName}>
                          {testName}
                        </TableCell>
                        <TableCell sx={{ py: 0.3, px: 0.75 }}>
                          <Chip
                            label={isPassed ? 'PASS' : 'FAIL'}
                            size="small"
                            variant="filled"
                            sx={{
                              height: 20, fontSize: '0.6rem', fontWeight: 700,
                              bgcolor: isPassed ? '#2e7d32' : '#c62828', color: '#fff',
                            }}
                          />
                        </TableCell>
                        <TableCell sx={{ py: 0.3, px: 0.75, fontSize: '0.6rem', fontFamily: 'monospace', color: 'text.secondary', width: 70 }}>
                          {dur != null ? `${Math.round(dur)}ms` : '--'}
                        </TableCell>
                        <TableCell sx={{ py: 0.3, px: 0.75, width: 80 }}>
                          {httpCallCount > 0 ? (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
                              <Typography variant="caption" fontSize="0.6rem" color="text.secondary">
                                {httpCallCount}
                              </Typography>
                              {hasExpandable && (
                                <ExpandMoreIcon sx={{
                                  fontSize: '0.7rem', color: 'text.disabled',
                                  transform: isExpanded ? 'rotate(180deg)' : 'none',
                                  transition: 'transform 0.15s',
                                }} />
                              )}
                            </Box>
                          ) : (
                            <Typography variant="caption" color="text.disabled" fontSize="0.55rem">--</Typography>
                          )}
                        </TableCell>
                      </TableRow>
                      {/* Expanded: error + HTTP calls */}
                      {hasExpandable && isExpanded && (
                        <TableRow>
                          <TableCell colSpan={5} sx={{ py: 0.5, px: 1.5, bgcolor: !isPassed ? '#ffebee' : 'grey.50' }}>
                            {/* Error message */}
                            {!isPassed && t.error && (
                              <Alert severity="error" variant="outlined"
                                sx={{ py: 0, px: 0.75, mb: 0.5, fontSize: '0.65rem', '& .MuiAlert-message': { py: 0.25 } }}>
                                {typeof t.error === 'string' ? t.error : JSON.stringify(t.error)}
                              </Alert>
                            )}
                            {/* HTTP calls list */}
                            {t.http_calls && t.http_calls.length > 0 && (
                              <Box sx={{ mt: 0.25 }}>
                                <Typography variant="caption" fontWeight={600} fontSize="0.6rem" color="text.secondary"
                                  sx={{ display: 'block', mb: 0.25 }}>
                                  HTTP Calls ({t.http_calls.length})
                                </Typography>
                                {t.http_calls.map((hc, hi) => (
                                  <Box key={hi} sx={{
                                    display: 'flex', alignItems: 'center', gap: 0.5,
                                    py: 0.2, borderBottom: '1px solid', borderColor: 'divider',
                                    '&:last-child': { borderBottom: 'none' },
                                  }}>
                                    <MethodBadge method={hc.method} />
                                    <Typography variant="caption" fontFamily="monospace" fontSize="0.6rem" sx={{
                                      flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                                    }} title={hc.url}>
                                      {hc.url.replace(/^https?:\/\/[^/]+/, '')}
                                    </Typography>
                                    <Chip label={hc.status_code} size="small" variant="outlined"
                                      color={hc.status_code < 400 ? 'success' : 'error'}
                                      sx={{ height: 16, fontSize: '0.55rem' }} />
                                    {hc.duration_ms != null && (
                                      <Typography variant="caption" color="text.secondary" fontSize="0.55rem">
                                        {hc.duration_ms}ms
                                      </Typography>
                                    )}
                                  </Box>
                                ))}
                              </Box>
                            )}
                          </TableCell>
                        </TableRow>
                      )}
                    </React.Fragment>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      ))}
    </Box>
  );
};

// ============================================================================
// Code Repository View (Tab 3) — workspace files + generated code
// ============================================================================

/** Filter out entries that look like function names rather than file paths */
const isFilePath = (p: string): boolean => {
  // Function names end with "()" — skip those
  if (p.endsWith('()')) return false;
  // Must contain a dot (extension) or slash (directory separator)
  return p.includes('.') || p.includes('/');
};

interface FolderGroup {
  folder: string;
  files: string[];
}

const CodeRepositoryView: React.FC<{
  projectId?: string;
  generatedCode?: GeneratedCodePayload | null;
}> = ({ projectId, generatedCode }) => {
  const [workspaceFiles, setWorkspaceFiles] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [selectedFile, setSelectedFile] = useState<string | null>(null);
  const [fileContent, setFileContent] = useState<string>('');
  const [fileLoading, setFileLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [collapsedFolders, setCollapsedFolders] = useState<Set<string>>(new Set());

  const generatedPaths = useMemo(() =>
    new Set((generatedCode?.files || []).map(f => f.path)),
    [generatedCode]
  );

  // Lazy load workspace files on first render
  useEffect(() => {
    if (loaded || !projectId) return;
    setLoaded(true);
    setLoading(true);
    setError(null);
    listWorkspaceFiles(projectId)
      .then(res => setWorkspaceFiles((res.files || []).filter(isFilePath)))
      .catch(err => setError(err.message))
      .finally(() => setLoading(false));
  }, [projectId, loaded]);

  // Merge workspace + generated file paths, filter, and group by folder
  const folderGroups: FolderGroup[] = useMemo(() => {
    const set = new Set<string>([...workspaceFiles]);
    (generatedCode?.files || []).forEach(f => {
      if (isFilePath(f.path)) set.add(f.path);
    });
    const sorted = Array.from(set).sort();

    // Group by directory
    const map: Record<string, string[]> = {};
    sorted.forEach(f => {
      const lastSlash = f.lastIndexOf('/');
      const folder = lastSlash >= 0 ? f.slice(0, lastSlash) : '.';
      if (!map[folder]) map[folder] = [];
      map[folder].push(f);
    });

    return Object.entries(map)
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([folder, files]) => ({ folder, files }));
  }, [workspaceFiles, generatedCode]);

  const totalFiles = folderGroups.reduce((s, g) => s + g.files.length, 0);

  const toggleFolder = (folder: string) => {
    setCollapsedFolders(prev => {
      const next = new Set(prev);
      if (next.has(folder)) next.delete(folder);
      else next.add(folder);
      return next;
    });
  };

  const handleFileClick = async (path: string) => {
    setSelectedFile(path);
    // If it's a generated file, use in-memory content
    if (generatedPaths.has(path)) {
      const file = generatedCode?.files?.find(f => f.path === path);
      setFileContent(file?.content || '');
      return;
    }
    // Otherwise fetch from workspace API
    if (!projectId) return;
    setFileLoading(true);
    try {
      const res = await getWorkspaceFileContent(projectId, path);
      setFileContent(res.content || '');
    } catch (err: any) {
      setFileContent(`// Error loading file: ${err.message}`);
    } finally {
      setFileLoading(false);
    }
  };

  if (!projectId && !generatedCode?.files?.length) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
        <Box sx={{ textAlign: 'center' }}>
          <FolderOpenIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
          <Typography variant="body2" color="text.secondary">No project context available.</Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', height: '100%' }}>
      {/* File tree */}
      <Box sx={{
        width: 240, minWidth: 200, borderRight: '1px solid', borderColor: 'divider',
        overflow: 'auto', bgcolor: 'grey.50',
      }}>
        <Typography variant="caption" fontWeight={700} fontSize="0.65rem" color="text.secondary"
          sx={{ display: 'block', px: 1, py: 0.75 }}>
          Files ({totalFiles})
        </Typography>
        {loading && <LinearProgress sx={{ mx: 1 }} />}
        {error && (
          <Typography variant="caption" color="error" sx={{ px: 1, display: 'block', fontSize: '0.6rem' }}>
            {error}
          </Typography>
        )}
        {totalFiles === 0 && !loading && (
          <Typography variant="caption" color="text.secondary" sx={{ px: 1, display: 'block', fontSize: '0.6rem' }}>
            No files in workspace yet.
          </Typography>
        )}
        {folderGroups.map(({ folder, files }) => {
          const isCollapsed = collapsedFolders.has(folder);
          const genCount = files.filter(f => generatedPaths.has(f)).length;
          return (
            <Box key={folder}>
              {/* Folder header */}
              <Box
                onClick={() => toggleFolder(folder)}
                sx={{
                  display: 'flex', alignItems: 'center', gap: 0.5,
                  px: 1, py: 0.4, cursor: 'pointer',
                  bgcolor: 'grey.100', borderBottom: '1px solid', borderColor: 'divider',
                  '&:hover': { bgcolor: 'grey.200' },
                }}
              >
                <ExpandMoreIcon sx={{
                  fontSize: '0.75rem', color: 'text.secondary',
                  transform: isCollapsed ? 'rotate(-90deg)' : 'none',
                  transition: 'transform 0.15s',
                }} />
                <FolderOpenIcon sx={{ fontSize: '0.7rem', color: '#e65100' }} />
                <Typography variant="caption" fontWeight={600} fontSize="0.6rem" sx={{ flex: 1 }}>
                  {folder === '.' ? '(root)' : folder}
                </Typography>
                <Typography variant="caption" color="text.secondary" fontSize="0.55rem">
                  {files.length}
                </Typography>
                {genCount > 0 && (
                  <Chip label={`${genCount} new`} size="small" variant="filled"
                    sx={{ height: 14, fontSize: '0.45rem', bgcolor: '#2e7d32', color: '#fff' }} />
                )}
              </Box>
              {/* Files in folder */}
              {!isCollapsed && files.map(f => {
                const fileName = f.split('/').pop() || f;
                return (
                  <Box
                    key={f}
                    onClick={() => handleFileClick(f)}
                    sx={{
                      display: 'flex', alignItems: 'center', gap: 0.5,
                      pl: 3, pr: 1, py: 0.35, cursor: 'pointer',
                      bgcolor: selectedFile === f ? 'primary.50' : undefined,
                      '&:hover': { bgcolor: 'action.hover' },
                    }}
                  >
                    <FileIcon sx={{ fontSize: '0.7rem', color: 'text.secondary' }} />
                    <Typography variant="caption" sx={{
                      fontSize: '0.6rem', flex: 1,
                      overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                    }} title={f}>
                      {fileName}
                    </Typography>
                    {generatedPaths.has(f) && (
                      <Chip label="NEW" size="small" variant="filled"
                        sx={{ height: 14, fontSize: '0.5rem', bgcolor: '#2e7d32', color: '#fff' }} />
                    )}
                  </Box>
                );
              })}
            </Box>
          );
        })}
      </Box>

      {/* File content */}
      <Box sx={{ flex: 1, overflow: 'auto', p: 1 }}>
        {selectedFile ? (
          <>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
              <Typography variant="caption" fontWeight={600} fontSize="0.7rem" sx={{
                fontFamily: 'monospace', flex: 1, overflow: 'hidden',
                textOverflow: 'ellipsis', whiteSpace: 'nowrap',
              }}>
                {selectedFile}
              </Typography>
              {generatedPaths.has(selectedFile) && (
                <Chip label="Generated" size="small" color="success" variant="outlined"
                  sx={{ height: 18, fontSize: '0.55rem' }} />
              )}
            </Box>
            {fileLoading ? (
              <LinearProgress />
            ) : (
              <Box sx={{
                fontFamily: 'monospace', fontSize: '0.65rem', whiteSpace: 'pre-wrap',
                wordBreak: 'break-all', p: 1, bgcolor: '#1e1e1e', color: '#d4d4d4',
                borderRadius: 1, maxHeight: 'calc(100% - 40px)', overflow: 'auto',
                lineHeight: 1.5,
              }}>
                {fileContent || '// Empty file'}
              </Box>
            )}
          </>
        ) : (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
            <Typography variant="caption" color="text.secondary">
              Select a file to view its content
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
};

// ============================================================================
// TMS Integration View (Tab 4)
// ============================================================================

const TMSRunRow: React.FC<{ run: TMSTestRun }> = ({ run }) => {
  const total = run.stats.total || 0;
  const passRate = total > 0 ? (run.stats.passed / total) * 100 : 0;
  const failRate = total > 0 ? (run.stats.failed / total) * 100 : 0;

  return (
    <Box sx={{
      p: 1, borderRadius: 1, border: '1px solid', borderColor: 'divider',
      display: 'flex', alignItems: 'center', gap: 1,
    }}>
      {/* Run ID link */}
      <Box sx={{ minWidth: 52 }}>
        {run.test_run_url ? (
          <Typography
            component="a"
            href={run.test_run_url}
            target="_blank"
            rel="noopener noreferrer"
            variant="caption"
            fontWeight={700}
            fontSize="0.75rem"
            sx={{ color: 'primary.main', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
          >
            #{run.test_run_id}
          </Typography>
        ) : (
          <Typography variant="caption" fontWeight={700} fontSize="0.75rem">
            #{run.test_run_id}
          </Typography>
        )}
      </Box>

      {/* Run name */}
      <Typography variant="caption" fontSize="0.65rem" color="text.secondary" noWrap sx={{ flex: 1, minWidth: 0 }}>
        {run.name || `Test Run`}
      </Typography>

      {/* Mini pass/fail bar */}
      <Box sx={{ width: 60, height: 6, borderRadius: 3, bgcolor: 'grey.200', overflow: 'hidden', display: 'flex', flexShrink: 0 }}>
        {passRate > 0 && <Box sx={{ width: `${passRate}%`, bgcolor: 'success.main' }} />}
        {failRate > 0 && <Box sx={{ width: `${failRate}%`, bgcolor: 'error.main' }} />}
      </Box>

      {/* Stats */}
      <Box sx={{ display: 'flex', gap: 0.5, flexShrink: 0 }}>
        <Typography variant="caption" fontSize="0.6rem" color="success.main" fontWeight={600}>
          {run.stats.passed}
        </Typography>
        <Typography variant="caption" fontSize="0.6rem" color="text.disabled">/</Typography>
        <Typography variant="caption" fontSize="0.6rem" color="error.main" fontWeight={600}>
          {run.stats.failed}
        </Typography>
      </Box>
    </Box>
  );
};

const TMSIntegrationView: React.FC<{ tmsIntegration?: TMSIntegrationInfo | null }> = ({ tmsIntegration }) => {
  if (!tmsIntegration) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
        <Box sx={{ textAlign: 'center' }}>
          <StorageIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
          <Typography variant="body2" color="text.secondary">Not connected to TMS</Typography>
          <Typography variant="caption" color="text.secondary">
            TMS integration data will appear here when a test run is linked.
          </Typography>
        </Box>
      </Box>
    );
  }

  const { framework, plugin_mode, reporting } = tmsIntegration;
  const runs = tmsIntegration.runs || [];
  const hasMultipleRuns = runs.length > 0;

  // Aggregate stats: prefer total_stats, fall back to legacy stats
  const aggStats = tmsIntegration.total_stats || (tmsIntegration.stats
    ? { total: Number(tmsIntegration.stats.total) || 0, passed: Number(tmsIntegration.stats.passed) || 0, failed: Number(tmsIntegration.stats.failed) || 0 }
    : { total: 0, passed: 0, failed: 0 });

  return (
    <Box sx={{ p: 1.5, overflow: 'auto', height: '100%' }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
        <StorageIcon sx={{ fontSize: '1.1rem', color: 'primary.main' }} />
        <Typography variant="subtitle2" fontWeight={700} fontSize="0.85rem">
          TMS Integration
        </Typography>
        <Typography variant="caption" color="text.secondary" fontSize="0.65rem">
          {hasMultipleRuns ? `${runs.length} Test Run${runs.length !== 1 ? 's' : ''}` : `Run #${tmsIntegration.test_run_id}`}
          {aggStats.total > 0 && ` · ${aggStats.total} Results`}
        </Typography>
        {/* Single-run: show link in header */}
        {!hasMultipleRuns && tmsIntegration.test_run_url && (
          <IconButton size="small" href={tmsIntegration.test_run_url} target="_blank" rel="noopener noreferrer"
            sx={{ p: 0.25, ml: 'auto' }}>
            <OpenInNewIcon sx={{ fontSize: '0.85rem' }} />
          </IconButton>
        )}
      </Box>

      {/* Aggregate Stats Cards */}
      <Box sx={{ display: 'flex', gap: 1, mb: 1.5, flexWrap: 'wrap' }}>
        {(['total', 'passed', 'failed'] as const).map(key => (
          <Box key={key} sx={{
            flex: 1, minWidth: 80, p: 1, borderRadius: 1,
            border: '1px solid', borderColor: 'divider', textAlign: 'center',
          }}>
            <Typography variant="h6" fontWeight={700} fontSize="1.1rem" color={
              key === 'passed' ? 'success.main' :
              key === 'failed' ? 'error.main' :
              'text.primary'
            }>
              {aggStats[key]}
            </Typography>
            <Typography variant="caption" color="text.secondary" fontSize="0.6rem" display="block"
              sx={{ textTransform: 'capitalize' }}>
              {key}
            </Typography>
          </Box>
        ))}
      </Box>

      {/* Per-Run Breakdown (multi-run only) */}
      {hasMultipleRuns && (
        <>
          <SectionLabel>Test Runs</SectionLabel>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, mb: 1.5 }}>
            {runs.map(run => (
              <TMSRunRow key={run.test_run_id} run={run} />
            ))}
          </Box>
        </>
      )}

      {/* Info Chips */}
      <Box sx={{ display: 'flex', gap: 0.75, mb: 1.5, flexWrap: 'wrap' }}>
        <Chip label={`Framework: ${framework}`} size="small" variant="outlined"
          sx={{ height: 22, fontSize: '0.65rem' }} />
        <Chip
          label={plugin_mode ? 'Plugin Mode' : 'Post-Execution'}
          size="small"
          variant="filled"
          color={plugin_mode ? 'primary' : 'default'}
          sx={{ height: 22, fontSize: '0.65rem' }}
        />
      </Box>

      {/* Reporting Info */}
      {reporting && (
        <>
          <SectionLabel>Reporting</SectionLabel>
          <Box sx={{
            p: 1, borderRadius: 1, border: '1px solid', borderColor: 'divider',
          }}>
            <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
              <Box>
                <Typography variant="caption" color="text.secondary" fontSize="0.6rem" display="block">
                  Mode
                </Typography>
                <Typography variant="caption" fontWeight={600} fontSize="0.7rem"
                  sx={{ textTransform: 'capitalize' }}>
                  {reporting.mode}
                </Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary" fontSize="0.6rem" display="block">
                  Results Reported
                </Typography>
                <Typography variant="caption" fontWeight={600} fontSize="0.7rem">
                  {reporting.results_reported}
                </Typography>
              </Box>
              {reporting.results_failed != null && (
                <Box>
                  <Typography variant="caption" color="text.secondary" fontSize="0.6rem" display="block">
                    Failed
                  </Typography>
                  <Typography variant="caption" fontWeight={600} fontSize="0.7rem" color="error.main">
                    {reporting.results_failed}
                  </Typography>
                </Box>
              )}
            </Box>
          </Box>
        </>
      )}
    </Box>
  );
};

// ============================================================================
// Main Panel
// ============================================================================

interface APIExecutionPanelProps {
  apiStepResults: APIStepResult[];
  onEndSession: () => void;
  onCancel: () => void;
  phase: string;
  generatedCode?: GeneratedCodePayload | null;
  agentActive?: boolean;
  agentTask?: string | null;
  agentToolEvents?: AgentToolActivity[];
  agentFindings?: AgentInvestigationFindings | null;
  tmsIntegration?: TMSIntegrationInfo | null;
  projectId?: string;
  discoveredEndpoints?: SlavePreflightEndpoint[];
  framework?: string;
}

const APIExecutionPanel: React.FC<APIExecutionPanelProps> = ({
  apiStepResults, onEndSession, onCancel, phase, generatedCode,
  agentActive = false, agentTask = null, agentToolEvents = [], agentFindings = null,
  tmsIntegration = null, projectId, discoveredEndpoints = [],
  framework,
}) => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [autoScroll, setAutoScroll] = useState(true);
  const [panelTab, setPanelTab] = useState(
    apiStepResults.length === 0 && generatedCode?.files?.length ? 3 : 0
  );

  // Auto-switch to Code Repository tab when code arrives but no results yet
  useEffect(() => {
    if (apiStepResults.length === 0 && generatedCode?.files?.length && panelTab === 0) {
      setPanelTab(3);
    }
  }, [generatedCode]);

  // Switch back to Execution tab when test results arrive
  useEffect(() => {
    if (apiStepResults.length > 0 && panelTab === 3) {
      setPanelTab(0);
    }
  }, [apiStepResults.length]);

  const isUITest = framework === 'playwright' || framework === 'selenium';

  // Split results by source
  const testExecutionResults = useMemo(() =>
    apiStepResults.filter(r => r.source === 'test_execution'), [apiStepResults]);
  const apiProbeResults = useMemo(() =>
    apiStepResults.filter(r => r.source === 'api_probe'), [apiStepResults]);
  const unsourcedResults = useMemo(() =>
    apiStepResults.filter(r => !r.source), [apiStepResults]);

  // Compute header stats from test execution results only (or all if no sourced entries exist)
  const statsSource = testExecutionResults.length > 0 ? testExecutionResults : apiStepResults;
  const total = statsSource.length;
  const totalSteps = statsSource[0]?.total_steps || total;
  const headerVerdicts = statsSource.map(r => getVerdict(r));
  const passed = headerVerdicts.filter(v => v === 'pass').length;
  const expectedFail = headerVerdicts.filter(v => v === 'expected_fail').length;
  const failed = headerVerdicts.filter(v => v === 'fail' || v === 'error').length;
  const running = headerVerdicts.filter(v => v === 'running').length;
  const completed = total - running;
  const progress = totalSteps > 0 ? (completed / totalSteps) * 100 : 0;
  const apiProbeCount = apiProbeResults.length;

  // Group by category (test execution only)
  const categories = statsSource.reduce<Record<string, number>>((acc, r) => {
    const cat = r.category || 'uncategorized';
    acc[cat] = (acc[cat] || 0) + 1;
    return acc;
  }, {});

  // Auto-scroll to bottom when new results arrive
  useEffect(() => {
    if (autoScroll && scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [total, autoScroll]);

  const handleScroll = () => {
    if (!scrollRef.current) return;
    const { scrollTop, scrollHeight, clientHeight } = scrollRef.current;
    setAutoScroll(scrollHeight - scrollTop - clientHeight < 50);
  };

  const isTerminal = phase === 'completed' || phase === 'failed' || phase === 'cancelled';

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', bgcolor: 'background.paper' }}>
      {/* Header */}
      <Box sx={{ px: 1.5, py: 1, borderBottom: '1px solid', borderColor: 'divider' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
          <ApiIcon sx={{ fontSize: '1.1rem', color: 'primary.main' }} />
          <Typography variant="subtitle2" fontWeight={700} fontSize="0.85rem">
            {isUITest ? 'UI Test Execution' : 'API Test Execution'}
          </Typography>
          <Chip
            label={isTerminal ? phase : running > 0 ? 'Running' : 'Waiting'}
            size="small"
            color={isTerminal ? (phase === 'completed' ? 'success' : 'error') : 'primary'}
            variant="outlined"
            sx={{ height: 20, fontSize: '0.65rem', textTransform: 'capitalize' }}
          />
        </Box>

        {/* Progress bar */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
          <LinearProgress
            variant="determinate"
            value={progress}
            sx={{ flex: 1, height: 6, borderRadius: 3 }}
          />
          <Typography variant="caption" color="text.secondary" fontSize="0.7rem">
            {completed}/{totalSteps}
          </Typography>
        </Box>

        {/* Stats row */}
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 0.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
            <PassedIcon sx={{ fontSize: '0.75rem', color: 'success.main' }} />
            <Typography variant="caption" fontWeight={600} fontSize="0.7rem" color="success.main">
              {passed}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
            <FailedIcon sx={{ fontSize: '0.75rem', color: 'error.main' }} />
            <Typography variant="caption" fontWeight={600} fontSize="0.7rem" color="error.main">
              {failed}
            </Typography>
          </Box>
          {expectedFail > 0 && (
            <Tooltip title="Negative/security tests that correctly received error responses" arrow>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
                <PassedIcon sx={{ fontSize: '0.75rem', color: 'warning.main' }} />
                <Typography variant="caption" fontWeight={600} fontSize="0.7rem" color="warning.main">
                  {expectedFail}
                </Typography>
              </Box>
            </Tooltip>
          )}
          {running > 0 && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
              <RunningIcon sx={{ fontSize: '0.75rem', color: 'info.main' }} />
              <Typography variant="caption" fontWeight={600} fontSize="0.7rem" color="info.main">
                {running}
              </Typography>
            </Box>
          )}
          <Box sx={{ ml: 'auto', display: 'flex', gap: 0.5 }}>
            {apiProbeCount > 0 && (
              <Chip label={`${apiProbeCount} probes`} size="small" variant="outlined"
                sx={{ height: 16, fontSize: '0.55rem', color: 'text.secondary' }} />
            )}
            {Object.entries(categories).map(([cat, count]) => (
              <Chip key={cat} label={`${cat}: ${count}`} size="small" variant="outlined"
                sx={{ height: 16, fontSize: '0.55rem', textTransform: 'capitalize' }} />
            ))}
          </Box>
        </Box>

        {/* Top-level panel tabs */}
        <Tabs
          value={panelTab}
          onChange={(_, v) => setPanelTab(v)}
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            minHeight: 30,
            '& .MuiTab-root': {
              minHeight: 30, fontSize: '0.65rem', py: 0,
              textTransform: 'none', minWidth: 'auto', px: 1.5,
              fontWeight: 600,
            },
            '& .MuiTabs-indicator': { height: 2 },
          }}
        >
          <Tab label="Execution" icon={<ApiIcon sx={{ fontSize: '0.8rem' }} />}
            iconPosition="start" sx={{ gap: 0.5 }} />
          <Tab label="Test Results" icon={<ScienceIcon sx={{ fontSize: '0.8rem' }} />}
            iconPosition="start" sx={{ gap: 0.5 }} />
          <Tab label="Summary" icon={<AssessmentIcon sx={{ fontSize: '0.8rem' }} />}
            iconPosition="start" sx={{ gap: 0.5 }} />
          <Tab
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                Code Repository
                {generatedCode?.files && generatedCode.files.length > 0 && (
                  <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: 'success.main' }} />
                )}
              </Box>
            }
            icon={<FolderOpenIcon sx={{ fontSize: '0.8rem' }} />}
            iconPosition="start"
            sx={{ gap: 0.5 }}
          />
          <Tab
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                TMS
                {tmsIntegration && (
                  <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: 'primary.main' }} />
                )}
              </Box>
            }
            icon={<StorageIcon sx={{ fontSize: '0.8rem' }} />}
            iconPosition="start"
            sx={{ gap: 0.5 }}
          />
        </Tabs>
      </Box>

      {/* Panel content — 5 tabs */}
      {panelTab === 0 ? (
        /* Tab 0: Execution — split by source */
        <Box
          ref={scrollRef}
          onScroll={handleScroll}
          sx={{ flex: 1, overflow: 'auto' }}
        >
          {apiStepResults.length === 0 ? (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
              <Typography variant="body2" color="text.secondary">
                {isUITest ? 'Waiting for UI test execution...' : 'Waiting for API test execution...'}
              </Typography>
            </Box>
          ) : (
            <>
              {/* Test Suite Execution section */}
              {testExecutionResults.length > 0 && (
                <>
                  <Box sx={{
                    display: 'flex', alignItems: 'center', gap: 0.5,
                    px: 1, py: 0.5, bgcolor: 'grey.100',
                    borderBottom: '1px solid', borderColor: 'divider',
                    position: 'sticky', top: 0, zIndex: 1,
                  }}>
                    <ScienceIcon sx={{ fontSize: '0.75rem', color: 'primary.main' }} />
                    <Typography variant="caption" fontWeight={700} fontSize="0.65rem" sx={{ flex: 1 }}>
                      {isUITest ? 'Playwright Test Suite' : 'Test Suite Execution'}
                    </Typography>
                    <Chip label={`${testExecutionResults.length} tests`} size="small" variant="outlined"
                      sx={{ height: 18, fontSize: '0.55rem' }} />
                  </Box>
                  {testExecutionResults.map((step, i) => (
                    <StepRow key={`test-${step.step_index}`} step={step} index={i} isUITest={isUITest} />
                  ))}
                </>
              )}

              {/* API Probing section */}
              {apiProbeResults.length > 0 && (
                <>
                  <Box sx={{
                    display: 'flex', alignItems: 'center', gap: 0.5,
                    px: 1, py: 0.5, bgcolor: 'grey.100',
                    borderBottom: '1px solid', borderColor: 'divider',
                    position: 'sticky', top: testExecutionResults.length > 0 ? 28 : 0, zIndex: 1,
                  }}>
                    <ApiIcon sx={{ fontSize: '0.75rem', color: 'text.secondary' }} />
                    <Typography variant="caption" fontWeight={700} fontSize="0.65rem" sx={{ flex: 1 }}>
                      API Probing
                    </Typography>
                    <Chip label={`${apiProbeResults.length} calls`} size="small" variant="outlined"
                      sx={{ height: 18, fontSize: '0.55rem' }} />
                  </Box>
                  {apiProbeResults.map((step, i) => (
                    <StepRow key={`probe-${step.step_index}`} step={step} index={i} />
                  ))}
                </>
              )}

              {/* Unsourced entries (legacy / fallback) */}
              {unsourcedResults.length > 0 && testExecutionResults.length === 0 && apiProbeResults.length === 0 && (
                unsourcedResults.map((step, i) => (
                  <StepRow key={step.step_index} step={step} index={i} />
                ))
              )}

              {(agentActive || agentFindings != null) && (
                <AgentActivityPanel
                  active={agentActive}
                  task={agentTask}
                  toolEvents={agentToolEvents}
                  findings={agentFindings}
                />
              )}
            </>
          )}
        </Box>
      ) : panelTab === 1 ? (
        /* Tab 1: Test Results */
        <Box sx={{ flex: 1, overflow: 'auto' }}>
          <TestResultsView results={apiStepResults} />
        </Box>
      ) : panelTab === 2 ? (
        /* Tab 2: Summary — uses test execution results for stats, all results for coverage */
        <Box sx={{ flex: 1, overflow: 'auto' }}>
          <SummaryView
            results={testExecutionResults.length > 0 ? testExecutionResults : apiStepResults}
            discoveredEndpoints={discoveredEndpoints}
            apiProbeCount={apiProbeCount}
            isUITest={isUITest}
          />
        </Box>
      ) : panelTab === 3 ? (
        /* Tab 3: Code Repository */
        <Box sx={{ flex: 1, overflow: 'hidden' }}>
          <CodeRepositoryView projectId={projectId} generatedCode={generatedCode} />
        </Box>
      ) : (
        /* Tab 4: TMS */
        <Box sx={{ flex: 1, overflow: 'auto' }}>
          <TMSIntegrationView tmsIntegration={tmsIntegration} />
        </Box>
      )}
    </Box>
  );
};

export default APIExecutionPanel;
